import React, { useEffect, useState } from 'react';
import { HashRouter as Router } from 'react-router-dom';
import { Redirect, Route, Switch } from 'react-router';
import {
  HeaderContainer,
  LoginContainer,
  OrdersContainer,
  OverviewContainer,
  PackageContainer,
  SignupContainer,
  StartContainer,
  OrderTopupsContainer,
  AddressesContainer,
} from 'containers';
import { Auth, configureAmplify } from 'lib/Amplify';
import SingleColumnLayout from 'layouts/SingleColumnLayout';
import { batch, useDispatch, useSelector } from 'react-redux';
import VerifyContainer from 'containers/VerifyContainer';
import {
  setAccountId,
  setEmailAddress,
  setEmailVerified,
  setUser,
} from 'store/actions/user';
import Spinner from 'components/Spinner';
import FindAccountContainer from 'containers/FindAccountContainer';
import { setAppBusy } from 'store/actions/system';
import {
  findSubscriptions,
  getAccount,
  getStore,
  getSubscription,
  getProductOptions,
} from 'gateways/Api';
import { setAccount } from 'store/actions/account';
import {
  reset as resetSubscriptionStore,
  setStore,
  setSubscription,
  setSubscriptions,
  setSubscriptionLoadFailed,
  setSubscriptionsList,
} from 'store/actions/subscription';
import ResetPasswordContainer from 'containers/ResetPasswordContainer';
import ChangePasswordContainer from 'containers/ChangePasswordContainer';
import config from 'Config';
import AutoLogout from 'components/AutoLogout';
import NavHistory from 'components/NavHistory';
import ChangeLensAmountContainer from 'containers/ChangeLensAmountContainer';
import ContactUsContainer from 'containers/ContactUsContainer';
import PrivacyPolicyFooterWidget from 'components/widgets/PrivacyPolicyFooterWidget';
import CookiesPolicyAcknowledgment from 'components/widgets/CookiesPolicyAcknowledgment';
import GTMVirtualPage from 'components/GTMVirtualPage';
import { routeProps as changePasswordRouteProps } from 'containers/paths/ChangePasswordPath';
import { routeProps as findAccountContainerProps } from 'containers/paths/FindAccountContainerPath';
import { routeProps as loginContainerProps } from 'containers/paths/LoginContainerPath';
import { routeProps as ordersRouteProps } from 'containers/paths/OrdersContainerPath';
import { routeProps as overvieRouteProps } from 'containers/paths/OverviewContainerPath';
import { routeProps as packageRouteProps } from 'containers/paths/PackageContainerPath';
import { routeProps as verifyContainerProps } from 'containers/paths/VerifyContainerPath';
import { routeProps as addressesRouteProps } from 'containers/paths/AddressesContainerPath';
import { routeProps as topupsRouteProps } from 'containers/paths/OrderTopupsContainerPath';
import { routeProps as resetPasswordContainerProps } from 'containers/paths/ResetPasswordContainerPath';
import { routeProps as signupContainerProps } from 'containers/paths/SignupContainerPath';
import { routeProps as startContainerProps } from 'containers/paths/StartContainerPath';
import { routeProps as changeLensAmountContainerProps } from 'containers/paths/ChangeLensAmountContainerPath';
import WrongCountryView from 'views/WrongCountryView';
import MenuContainer from 'containers/MenuContainer';

configureAmplify();

const App = () => {
  const dispatch = useDispatch();
  const appBusy = useSelector((state) => state.appBusy);
  const message = useSelector((state) => state.message);
  const email = useSelector((state) => state.email);
  const emailVerified = useSelector((state) => state.emailVerified);
  const accountId = useSelector((state) => state.accountId);
  const user = useSelector((state) => state.user);
  const subscriptionsList = useSelector((state) => state.subscriptionsList);
  const subscriptions = useSelector((state) => state.subscriptions);
  const store = useSelector((state) => state.store);

  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [fetchingUser, setFetchingUser] = useState(true);

  useEffect(() => {
    Auth.currentAuthenticatedUser()
      .then((cognitoUser) => {
        dispatch(setUser(cognitoUser));
      })
      .catch(() => {
        // TODO: Check the error type, if user does not exist, clear the state
        dispatch(setUser(null));
      })
      .finally(() => setFetchingUser(false));
  }, [dispatch, setFetchingUser]);

  useEffect(() => {
    if (fetchingUser) {
      return;
    }

    if (user) {
      Auth.userAttributes(user)
        .then((attrs) => {
          attrs.forEach((attr) => {
            if (attr.Name === 'email_verified' && attr.Value === 'true') {
              dispatch(setEmailVerified(true));
            }
            if (attr.Name === 'email') {
              dispatch(setEmailAddress(attr.Value));
            }
            if (attr.Name === 'custom:accountId') {
              dispatch(setAccountId(attr.Value));
            }
          });
        })
        .catch(() => {
          dispatch(setUser(null));
        })
        .finally(() => {
          dispatch(setAppBusy(false));
          setLoading(false);
        });
    } else {
      batch(() => {
        dispatch(setEmailVerified(false));
        dispatch(setEmailAddress(null));
        dispatch(setAccountId(null));
        dispatch(setAppBusy(false));
        dispatch(setAccount(null));
        dispatch(resetSubscriptionStore());
      });
      setLoading(false);
    }
  }, [dispatch, fetchingUser, user]);

  useEffect(() => {
    if (!accountId) {
      return;
    }
    getAccount()
      .then((account) => getStore(account.mpp_store_id).then((res) => [
        res.data.message.store,
        account,
      ]))
      .then(([storeReturned, account]) => {
        batch(() => {
          dispatch(setStore(storeReturned));
          dispatch(setAccount(account));
        });
      })
      .catch(() => dispatch(setSubscriptionLoadFailed()));

    findSubscriptions()
      .then((subs) => dispatch(setSubscriptionsList(subs)))
      .catch(() => dispatch(setSubscriptionLoadFailed()));
  }, [dispatch, accountId]);

  useEffect(() => {
    if (
      !subscriptionsList
      || !subscriptionsList.activeSubs.length
      || subscriptions.length
    ) {
      return;
    }
    setUpdating(true);

    Promise.all(
      subscriptionsList.activeSubs.map((sub) => getSubscription(sub.id)
        .then((res) => res.data.message.subscription)),
    )
      .then((fetchedSubs) => {
        dispatch(setSubscriptions(fetchedSubs));
      })
      .catch(() => dispatch(setSubscriptionLoadFailed()))
      .finally(() => setUpdating(false));
  }, [dispatch, subscriptionsList, subscriptions]);

  useEffect(() => {
    if (!subscriptions || !subscriptions.length) return;
    const subSummary = subscriptionsList.activeSubs[subscriptionsList.currentSubscriptionIndex];
    const currentSubscription = subscriptions.find(
      (sub) => sub.id === subSummary.id,
    );
    dispatch(setSubscription(currentSubscription));
  }, [subscriptionsList, subscriptions, dispatch]);

  const logoutMinutes = 60;
  const logoutTimeout = logoutMinutes * 60 * 1000;

  const { footerLinks } = config;

  if (loading) {
    return <Spinner />;
  }

  const renderRoutes = () => {
    if (store && store.address.country !== config.countryCode) {
      dispatch(setAppBusy(false));
      return (
        <WrongCountryView
          alternativeCountry={store.address.country}
          siteLabel={config.alternativeSites[store.address.country].label}
          siteUrl={config.alternativeSites[store.address.country].url}
        />
      );
    }

    return (
      <Switch>
        <Route path="/" exact>
          {!user && <Redirect to="/start" />}
          {!user && email && !emailVerified && <Redirect to="/verify" />}
          {user && !accountId && <Redirect to="/find_account" />}
          {user && accountId && <Redirect to="/overview" />}
        </Route>

        <Route {...addressesRouteProps} component={AddressesContainer} />
        <Route {...changePasswordRouteProps} component={ChangePasswordContainer} />
        <Route {...findAccountContainerProps} component={FindAccountContainer} />
        <Route {...loginContainerProps} component={LoginContainer} />
        <Route {...ordersRouteProps} component={OrdersContainer} />
        <Route {...overvieRouteProps} component={OverviewContainer} />
        <Route {...packageRouteProps} component={PackageContainer} />
        {/* hid for now because payment method is not fully finished and tested */}
        {/* <Route {...paymentMethodRouteProps} component={PaymentMethodContainer} /> */}
        <Route {...resetPasswordContainerProps} component={ResetPasswordContainer} />
        <Route {...signupContainerProps} component={SignupContainer} />
        <Route {...startContainerProps} component={StartContainer} />
        <Route {...verifyContainerProps} component={VerifyContainer} />

        <Route
          {...changeLensAmountContainerProps}
          render={() => (
            <ChangeLensAmountContainer getProductOptionsFunc={getProductOptions} />
          )}
        />
        {/* hid for now because payment method is not fully finished and tested */}
        {/* <Route {...changeBankDetailsContainerProps} component={ChangeBankDetailsContainer} /> */}
        <Route
          {...topupsRouteProps}
          render={() => (
            <OrderTopupsContainer getProductOptionsFunc={getProductOptions} />
          )}
        />
      </Switch>
    );
  };

  return (
    <Router>
      <NavHistory />
      <GTMVirtualPage />
      <AutoLogout timeout={logoutTimeout} user={user} />
      <SingleColumnLayout
        appBusy={appBusy || updating}
        message={message}
        header={<HeaderContainer />}
        menu={<MenuContainer />}
      >
        {renderRoutes()}
        <ContactUsContainer />
        <PrivacyPolicyFooterWidget links={footerLinks} />
        <CookiesPolicyAcknowledgment />
      </SingleColumnLayout>
    </Router>
  );
};

App.displayName = 'App';

export default App;
